import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { BehaviorSubject, merge, Observable, of, timer } from 'rxjs';
import {
  map,
  filter,
  debounce,
  distinctUntilChanged,
  switchMap,
  mergeMap,
} from 'rxjs/operators';
import { fromFetch } from 'rxjs/fetch';
import { NAVIGATION_ENDPOINTS } from '../../constants/urls';
import {
  SearchResultObject,
  SearchState,
  SearchStatus,
} from '../../../search/types';
import { getEndpoint } from '../../utils';
import { AmplitudeSearchEvents, useAmplitude } from '../../hooks/useAmplitude';
import { useSearchableMenuItems } from '../../../search/useSearchableMenuItems';
import {
  SearchResultNew,
  SearchResultComponent,
} from '../../../search/SearchResultNew';
import { SEARCH_TERM_PARAM_KEY } from '../../../search/Constants';
import { SearchContext } from '../../../search';
import { useVariant } from '../hooks/useVariant';
import { getSearchSubmitBehavior } from '../../../search/searchSubmitBehavior';
import { appendSearchHistory } from '../../../search/searchHistory';
import {
  SEARCH_CHANGE_EVENT,
  SEARCH_SUBMIT_EVENT,
  SearchChangeEventDetail,
  SearchSubmitEventDetail,
} from '../../../search/searchEvents';

const DEBOUNCE_TIME = 500;
const MIN_CHAR_LENGTH = 3;

function searchCount(keyword: string): Observable<Partial<SearchState>> {
  const path = `${NAVIGATION_ENDPOINTS.SEARCH_COUNT}?q=${encodeURIComponent(
    keyword,
  )}`;
  return fromFetch(getEndpoint(window.location, path), {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }).pipe(
    mergeMap((res) => {
      return !res?.ok
        ? of({ error: { message: res.statusText ?? 'Network Error' } })
        : res.json();
    }),
    map((response) => {
      return response.success
        ? {
            status: SearchStatus.Success,
            data: response.data,
          }
        : {
            status: SearchStatus.Error,
            error: response?.error,
          };
    }),
  );
}

function quickSearch(keyword: string): Observable<Partial<SearchState>> {
  const path = `${
    NAVIGATION_ENDPOINTS.SEARCH_QUICK_SEARCH
  }?q=${encodeURIComponent(keyword)}`;
  return fromFetch(getEndpoint(window.location, path), {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }).pipe(
    mergeMap((res) => {
      return !res?.ok
        ? of({ error: { message: res.statusText ?? 'Network Error' } })
        : res.json();
    }),
    map((response) => {
      return response.success
        ? {
            status: SearchStatus.Success,
            data: response.data,
          }
        : {
            status: SearchStatus.Error,
            error: response?.error,
          };
    }),
  );
}
function performSearchCount(keyword: string): Observable<Partial<SearchState>> {
  if (keyword.length === 0) {
    return of({ status: SearchStatus.Idle, data: [] });
  }

  const searchCountObservable = merge(
    of({ status: SearchStatus.Loading, data: [] }),
    searchCount(keyword).pipe(
      map((state) => {
        if (state.status === SearchStatus.Success && state.data) {
          return {
            ...state,
            data: [...state.data],
          };
        }
        if (state.status === SearchStatus.Error) {
          return {
            status: SearchStatus.Success,
            data: [],
          };
        }
        return state;
      }),
    ),
  );

  return searchCountObservable;
}

function performSearch(
  keyword: string,
  status: SearchStatus,
  searchNavItems: (keyword: string) => SearchResultObject,
  logEvent,
): Observable<Partial<SearchState>> {
  if (keyword.length === 0) {
    return of({ status: SearchStatus.Idle, data: [] });
  }
  logEvent(AmplitudeSearchEvents.QUICK_SEARCH, { searchTerm: keyword });

  let navResult;
  try {
    navResult = searchNavItems(keyword);
  } catch (e) {
    console.error(e);
  }

  const hasNavResult = !!navResult?.objects?.length;

  const quickSearchObservable = merge(
    of({ status: SearchStatus.Loading, data: [] }),
    quickSearch(keyword).pipe(
      map((state) => {
        if (
          state.status === SearchStatus.Success &&
          state.data &&
          hasNavResult
        ) {
          return {
            ...state,
            data: [...state.data, navResult],
          };
        }
        if (state.status === SearchStatus.Error && hasNavResult) {
          return {
            status: SearchStatus.Success,
            data: [navResult],
          };
        }
        return state;
      }),
    ),
  );

  return quickSearchObservable;
}

/** `z-search` exposes its current query through this property. */
type ZSearchElement = Element & { searchValue: string };

const SearchComponent = ({ menuElement }) => {
  const param = new URLSearchParams(window.location.search);
  const defaultSearchTerm = param.get(SEARCH_TERM_PARAM_KEY) || '';
  const variant = useVariant();
  const searchResultRef = useRef<SearchResultComponent>(null);
  // Get or create the content container - computed on every render to handle external removal
  const getContentContainer = (): HTMLDivElement | null => {
    if (!menuElement) return null;

    let container = menuElement.querySelector(
      '.global-nav-search-content-container',
    ) as HTMLDivElement | null;

    if (!container) {
      container = document.createElement('div');
      container.slot = 'global-nav-search-results';
      container.className = 'global-nav-search-content-container';
      menuElement.appendChild(container);
    }

    return container;
  };
  const contentContainer = getContentContainer();
  const [subject] = useState<BehaviorSubject<string>>(
    new BehaviorSubject(defaultSearchTerm),
  );
  const [keyword, setKeyword] = useState<string>(defaultSearchTerm);
  // Read by the submit handler so its listener binds once per host instead of
  // re-subscribing on every keystroke.
  const keywordRef = useRef(keyword);
  keywordRef.current = keyword;
  const [quickSearchState, setQuickSearchState] = useState<SearchState>({
    status: SearchStatus.Idle,
    data: [],
  });
  const [searchCountState, setSearchCountState] = useState<SearchState>({
    status: SearchStatus.Idle,
    data: [],
  });
  const [debounceEnabled, setDebounceEnabled] = useState(true);

  const { logEvent } = useAmplitude();
  const { menus, searchMenu } = useSearchableMenuItems();

  const setKeywordByChildren = useCallback(
    (keyword) => {
      setDebounceEnabled(false);
      setKeyword(keyword);
      subject.next(keyword);
    },
    [subject, setKeyword, setDebounceEnabled],
  );

  const handleSearchSubmit = useCallback(
    (submitValue?: string) => {
      const trimmedKeyword = (submitValue ?? keywordRef.current).trim();
      if (trimmedKeyword.length < MIN_CHAR_LENGTH) {
        return;
      }

      const behavior = getSearchSubmitBehavior(variant);
      if (behavior.type !== 'advancedSearch') {
        return;
      }

      if (behavior.href) {
        appendSearchHistory(trimmedKeyword);
        const separator = behavior.href.includes('?') ? '&' : '?';
        window.location.href = `${
          behavior.href
        }${separator}${SEARCH_TERM_PARAM_KEY}=${encodeURIComponent(
          trimmedKeyword,
        )}`;
        return;
      }

      searchResultRef.current?.goToAdvancedPage();
    },
    [variant],
  );

  // Handle search-change event from web component
  useEffect(() => {
    if (!menuElement) return;

    const handleSearchChange = (e: CustomEvent<SearchChangeEventDetail>) => {
      const searchValue = e.detail?.value || '';

      setKeyword(searchValue);
      subject.next(searchValue);
      setDebounceEnabled(true);

      // Clear results immediately when the box is empty (do not wait on rxjs).
      // App-switch host rebuild is handled by remounting SearchComponent in the controller.
      if (!searchValue.trim()) {
        setQuickSearchState({ status: SearchStatus.Idle, data: [] });
        setSearchCountState({ status: SearchStatus.Idle, data: [] });
      }
    };

    menuElement.addEventListener(SEARCH_CHANGE_EVENT, handleSearchChange);

    return () => {
      menuElement.removeEventListener(SEARCH_CHANGE_EVENT, handleSearchChange);
    };
  }, [menuElement, subject]);

  // z-search owns keyboard semantics and emits `search-submit` on Enter; nav-v2
  // only decides what submit means per variant. No-op on zds3 versions that do
  // not emit the event yet.
  useEffect(() => {
    if (!menuElement) return;

    const handleSubmitEvent = (event: Event) => {
      const detail = (event as CustomEvent<SearchSubmitEventDetail>).detail;
      handleSearchSubmit(detail?.value);
    };

    menuElement.addEventListener(SEARCH_SUBMIT_EVENT, handleSubmitEvent);

    return () => {
      menuElement.removeEventListener(SEARCH_SUBMIT_EVENT, handleSubmitEvent);
    };
  }, [menuElement, handleSearchSubmit]);

  // Submitting a search lands on the fullscreen page with ?searchTerm=, and the
  // results below are already seeded from it. z-search owns its own input, so
  // the term has to be pushed back in or the box would read as empty while
  // showing results for it. Legacy nav kept the term visible the same way.
  useEffect(() => {
    if (!menuElement || !defaultSearchTerm) return;

    const host = menuElement as ZSearchElement;
    if (host.searchValue === defaultSearchTerm) return;

    // Assigning re-emits search-change; the keyword is already in state, and
    // distinctUntilChanged drops the duplicate before it reaches the APIs.
    host.searchValue = defaultSearchTerm;
  }, [menuElement, defaultSearchTerm]);

  // Set up the search observables (copied from your original code)
  useEffect(() => {
    const searchTermObservable = subject.pipe(
      map((keyword) => keyword.trim()),
      filter(
        (keyword) => keyword.length === 0 || keyword.length >= MIN_CHAR_LENGTH,
      ),
      debounce((keyword) =>
        keyword.length > 0 && debounceEnabled ? timer(DEBOUNCE_TIME) : timer(0),
      ),
      distinctUntilChanged(),
    );

    const searchCountSubscription = searchTermObservable
      .pipe(
        switchMap((keyword) => {
          return performSearchCount(keyword);
        }),
      )
      .subscribe((searchCountValue) => {
        setSearchCountState((prev) =>
          Object.assign({}, prev, searchCountValue),
        );
      });

    const quickSearchSubscription = searchTermObservable
      .pipe(
        switchMap((keyword) => {
          return performSearch(
            keyword,
            quickSearchState.status,
            searchMenu,
            logEvent,
          );
        }),
      )
      .subscribe((quickSearchValue) => {
        setQuickSearchState((prev) =>
          Object.assign({}, prev, quickSearchValue),
        );
      });

    return () => {
      searchCountSubscription.unsubscribe();
      quickSearchSubscription.unsubscribe();
    };
  }, [debounceEnabled, menus]);

  // Don't render anything if we don't have a container or it's not in DOM
  if (!contentContainer || !contentContainer.isConnected) {
    return null;
  }

  return (
    <>
      {ReactDOM.createPortal(
        <SearchContext.Provider
          value={{ keyword, setKeyword: setKeywordByChildren }}
        >
          <div
            className="global-nav-search-react-content"
            style={{ width: '37.125rem' }}
          >
            <SearchResultNew
              ref={searchResultRef}
              state={[quickSearchState, searchCountState]}
              keyword={keyword}
            />
          </div>
        </SearchContext.Provider>,
        contentContainer,
      )}
    </>
  );
};

export default SearchComponent;
